import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { AuthService } from '../services/auth.service';
import { DataService } from '../services/data.service';

@Component({
    selector: 'app-dashboard',
    standalone: true,
    imports: [CommonModule, CardModule, ButtonModule],
    template: `
        <div class="dashboard-container">
            <p-card>
                <ng-template pTemplate="header">
                    <div class="card-header">
                        <h2>Welcome to the Auth Sample App</h2>
                    </div>
                </ng-template>

                <div class="actions">
                    <button pButton type="button" label="Fetch Communities" icon="pi pi-building" class="p-button-success" (click)="fetchCommunities()" [loading]="isLoadingCommunities"></button>
                    <button pButton type="button" label="Get Current Session" icon="pi pi-user" class="p-button-primary" (click)="getCurrentSession()" [loading]="isLoadingSession"></button>
                    <button pButton type="button" label="Clear Tokens (Test Refresh)" icon="pi pi-refresh" class="p-button-warning" (click)="refreshTokens()"></button>
                    <button pButton type="button" label="Logout" icon="pi pi-sign-out" class="p-button-secondary" (click)="logout()"></button>
                </div>

                <div class="token-info">
                    <h3>Current Session</h3>
                    <div class="info-grid" *ngIf="sessionData">
                        <div class="info-item" *ngFor="let item of getObjectEntries(sessionData)">
                            <strong>{{ item.key }}:</strong> {{ formatValue(item.value) }}
                        </div>
                    </div>
                    <div class="no-token" *ngIf="!sessionData && !isLoadingSession">No session data loaded. Click "Get Current Session" to fetch session information.</div>
                    <div class="no-token" *ngIf="isLoadingSession">Loading session data...</div>
                </div>

                <div class="token-info">
                    <h3>Communities</h3>
                    <div class="info-grid" *ngIf="communities">
                        <div class="info-item" *ngFor="let item of getObjectEntries(communities)">
                            <strong>{{ item.key }}:</strong> {{ formatValue(item.value) }}
                        </div>
                    </div>
                    <div class="no-token" *ngIf="!communities && !isLoadingCommunities">No communities data loaded. Click "Fetch Communities" to load data.</div>
                    <div class="no-token" *ngIf="isLoadingCommunities">Loading communities...</div>
                </div>

                <div class="token-info">
                    <h3>ID Token Properties</h3>
                    <div class="info-grid" *ngIf="idTokenPayload">
                        <div class="info-item" *ngFor="let item of getObjectEntries(idTokenPayload)">
                            <strong>{{ item.key }}:</strong> {{ formatValue(item.value) }}
                        </div>
                    </div>
                    <div class="no-token" *ngIf="!idTokenPayload">No ID token found in cookies</div>
                </div>

                <div class="token-info">
                    <h3>Access Token Properties</h3>
                    <div class="info-grid" *ngIf="accessTokenPayload">
                        <div class="info-item" *ngFor="let item of getObjectEntries(accessTokenPayload)">
                            <strong>{{ item.key }}:</strong> {{ formatValue(item.value) }}
                        </div>
                    </div>
                    <div class="no-token" *ngIf="!accessTokenPayload">No access token found in cookies</div>
                </div>
            </p-card>
        </div>
    `,
    styles: [
        `
            .dashboard-container {
                padding: 2rem;
                max-width: 1000px;
                margin: 0 auto;
            }

            .card-header {
                text-align: center;
                padding: 1rem;
            }

            .token-info {
                margin-bottom: 2rem;
            }

            .info-grid {
                display: grid;
                gap: 0.5rem;
                margin-top: 1rem;
            }

            .info-item {
                padding: 0.75rem;
                background: var(--surface-100);
                border-radius: 4px;
                word-break: break-all;
            }

            .actions {
                text-align: center;
                display: flex;
                gap: 1rem;
                justify-content: center;
                flex-wrap: wrap;
                margin-bottom: 2rem;
                padding: 1rem 0;
            }

            .no-token {
                padding: 1rem;
                background: var(--surface-200);
                border-radius: 4px;
                color: var(--text-color-secondary);
                font-style: italic;
            }
        `
    ]
})
export class DashboardComponent implements OnInit {
    idTokenPayload: any = null;
    accessTokenPayload: any = null;
    sessionData: any = null;
    isLoadingSession = false;
    communities: any = null;
    isLoadingCommunities = false;

    constructor(
        private authService: AuthService,
        private dataService: DataService
    ) {}

    ngOnInit() {
        this.idTokenPayload = this.authService.getCurrentUser();
        this.accessTokenPayload = this.authService.getAccessTokenPayload();

        console.log('ID Token Payload:', this.idTokenPayload);
        console.log('Access Token Payload:', this.accessTokenPayload);
        console.log('All cookies:', document.cookie);
    }

    getObjectEntries(obj: any): { key: string; value: any }[] {
        return Object.entries(obj || {}).map(([key, value]) => ({ key, value }));
    }

    formatValue(value: any): string {
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'number' && value > 1000000000) {
            return new Date(value * 1000).toLocaleString();
        }
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    }

    refreshTokens() {
        this.authService.refreshTokens().subscribe({
            next: (success) => {
                console.log('Token refresh result:', success);
            },
            error: (error) => {
                console.error('Token refresh error:', error);
            }
        });
    }

    getCurrentSession() {
        this.isLoadingSession = true;
        this.authService.getCurrentUserSession().subscribe({
            next: (sessionData) => {
                this.sessionData = sessionData;
                this.isLoadingSession = false;
                console.log('Session data loaded:', sessionData);
            },
            error: (error) => {
                console.error('Error fetching session:', error);
                this.isLoadingSession = false;
            }
        });
    }

    fetchCommunities() {
        this.isLoadingCommunities = true;
        this.dataService.checkApi().subscribe({
            next: (data) => {
                this.communities = data;
                this.isLoadingCommunities = false;
                console.log('Communities data loaded:', data);
            },
            error: (error) => {
                console.error('Error fetching communities:', error);
                this.isLoadingCommunities = false;
            }
        });
    }

    logout() {
        this.authService.logout();
    }
}
